import React, { useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { HelmetProvider } from 'react-helmet-async';
import Navbar from './components/Navbar';
import ErrorBoundary from './components/ErrorBoundary';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Lazy load components
const Home = React.lazy(() => import('./pages/Home'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const TradingJournal = React.lazy(() => import('./pages/TradingJournal'));
const PremiumChart = React.lazy(() => import('./pages/PremiumChart/PremiumChart'));
const Login = React.lazy(() => import('./pages/Auth/Login'));
const Register = React.lazy(() => import('./pages/Auth/Register'));
const ForgotPassword = React.lazy(() => import('./pages/Auth/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./pages/Auth/ResetPassword'));
const NotFound = React.lazy(() => import('./pages/NotFound'));
const PaperTradingPage = React.lazy(() => import('./pages/PaperTrading/PaperTradingPage'));
const AiChatBot = React.lazy(() => import('./pages/AiChatBot'));
const About = React.lazy(() => import('./pages/About'));
const Profile = React.lazy(() => import('./pages/Profile'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const TermsAndConditions = React.lazy(() => import('./pages/TermsAndConditions'));
const ContactUs = React.lazy(() => import('./pages/ContactUs'));

// Loading component
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-secondary"></div>
  </div>
);

const PrivateRoute = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const isAuthenticated = localStorage.getItem('token') !== null;
  
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  return <>{children}</>;
};

const TitleUpdater = () => {
  const location = useLocation();

  useEffect(() => {
    const getPageTitle = (pathname: string) => {
      switch (pathname) {
        case '/':
          return 'SarvKalyan - Professional Trading Platform';
        case '/dashboard':
          return 'Dashboard - SarvKalyan';
        case '/zone-scanner':
          return 'Zone Scanner - SarvKalyan';
        case '/paper-trading':
          return 'Paper Trading - SarvKalyan';
        case '/trading-journal':
          return 'Trading Journal - SarvKalyan';
        case '/ai-chatbot':
          return 'AI Chat Assistant - SarvKalyan';
        case '/about':
          return 'About Us - SarvKalyan';
        case '/blog':
          return 'Trading Insights & Education - SarvKalyan';
        case '/reset-password':
          return 'Reset Password - SarvKalyan';
        case '/privacy-policy':
          return 'Privacy Policy - SarvKalyan';
        case '/terms-and-conditions':
          return 'Terms and Conditions - SarvKalyan';
        case '/contact-us':
          return 'Contact Us - SarvKalyan';
        default:
          return 'SarvKalyan';
      }
    };

    document.title = getPageTitle(location.pathname);
  }, [location]);

  return null;
};

function App() {
  return (
    <AuthProvider>
      <HelmetProvider>
        <ErrorBoundary>
          <Router>
            <AppContent />
          </Router>
        </ErrorBoundary>
      </HelmetProvider>
    </AuthProvider>
  );
}

function AppContent() {
  const { user } = useAuth();
  const location = useLocation();

  return (
    <div className="min-h-screen bg-background text-text bg-gradient-radial from-background-lighter via-background to-background">
      <TitleUpdater />
      <Navbar />
      <ToastContainer position="top-right" autoClose={3000} />
      <Suspense fallback={<LoadingSpinner />}>
        <main className="container mx-auto px-4 py-8">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/premium-chart" element={
              <PrivateRoute>
                <PremiumChart />
              </PrivateRoute>
            } />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/about" element={<About />} />
            <Route path="/ai-chatbot" element={
              <PrivateRoute>
                <AiChatBot />
              </PrivateRoute>
            } />
            <Route path="/paper-trading" element={
              <PrivateRoute>
                <PaperTradingPage />
              </PrivateRoute>
            } />
            <Route path="/dashboard" element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            } />
            <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
            <Route path="/trading-journal" element={<PrivateRoute><TradingJournal /></PrivateRoute>} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="/contact-us" element={<ContactUs />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
      </Suspense>
    </div>
  );
}

export default App;